import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Modal, useToast } from '../ui';

interface PendingNotice {
  id: string;
  typeLabel: string;
  version: string;
  title: string;
  summary: string;
  content: string;
}

/**
 * Asks an owner to accept a SARTHI document published as requiring
 * acceptance - a new version of the terms, say - that they have not accepted
 * yet.
 *
 * Nothing appears unless such a document has been published: existing users
 * are never asked again just because a version changed. "Later" puts it off
 * for this session only; it comes back until it is accepted.
 */
export function PendingAcceptances() {
  const { session } = useAuth();
  const isOwner = session?.role === 'OWNER';
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [later, setLater] = useState(false);
  const [reading, setReading] = useState<PendingNotice | null>(null);

  const pending = useQuery({
    queryKey: ['privacy-pending-acceptances'],
    queryFn: () => api.get<PendingNotice[]>('/api/privacy/acceptances/pending'),
    enabled: isOwner,
    staleTime: 10 * 60_000,
  });

  const accept = useMutation({
    mutationFn: (noticeId: string) => api.post('/api/privacy/acceptances', { noticeId }),
    onSuccess: () => {
      push('Accepted - thank you', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['privacy-pending-acceptances'] });
    },
    onError: fail,
  });

  const notices = pending.data ?? [];
  if (!isOwner || later || notices.length === 0) return null;

  return (
    <Modal
      title="Updated documents to accept"
      onClose={() => setLater(true)}
      footer={
        <button className="btn" onClick={() => setLater(true)}>
          Later
        </button>
      }
    >
      <div className="col gap-14">
        <p className="small muted" style={{ margin: 0 }}>
          As the owner, you accept these on behalf of your hotel.
        </p>
        {notices.map((notice) => (
          <div key={notice.id} className="privacy-notice">
            <div className="row between">
              <b>
                {notice.title} <span className="xs muted">v{notice.version}</span>
              </b>
            </div>
            <p className="small" style={{ margin: 0 }}>
              {notice.summary}{' '}
              <button type="button" className="linklike" onClick={() => setReading(notice)}>
                Read it
              </button>
            </p>
            <div>
              <button
                className="btn primary sm"
                disabled={accept.isPending}
                onClick={() => accept.mutate(notice.id)}
              >
                I accept
              </button>
            </div>
          </div>
        ))}
      </div>
      {reading ? (
        <Modal title={reading.title} onClose={() => setReading(null)}>
          <div className="small privacy-doc">{reading.content}</div>
        </Modal>
      ) : null}
    </Modal>
  );
}
